import * as Yup from "yup";
import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import {
  Link,
  Stack,
  Alert,
  IconButton,
  InputAdornment,
  Box,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";

import { PATH_AUTH } from "../../../routes/paths";

import useAuth from "../../../hooks/useAuth";
import useIsMountedRef from "../../../hooks/useIsMountedRef";

import Iconify from "../../../components/Iconify";
import {
  FormProvider,
  RHFTextField,
  RHFCheckbox,
} from "../../../components/hook-form";
import { setSession } from "src/utils/jwt";
import axios from "axios";
import { loginWithPasswordApi } from "src/api/user";
import { useSnackbar } from "notistack";

export default function LoginForm() {
  const { login } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const isMountedRef = useIsMountedRef();

  const [showPassword, setShowPassword] = useState(false);

  const LoginSchema = Yup.object().shape({
    username: Yup.string().required("Email is required"),
    password: Yup.string().required("Password is required"),
  });

  const defaultValues = {
    username: "",
    password: "",
    remember: false,
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    reset,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = methods;

  const onSubmit = async (data) => {
    try {
      const response = await loginWithPasswordApi({
        username: data.username,
        password: data.password,
      });
      if (response) {
        const { tokenType, accessToken, refreshToken, expiresIn } =
          response.data;
        const isToken = {
          tokenType,
          accessToken,
          refreshToken,
          expiresIn,
        };
        if (response.data) {
          await setSession(isToken);
          const { data } = await axios.get(
            `${process.env.REACT_APP_HOST}/v1/users/info`,
            {
              headers: {
                Authorization: `Bearer ${response.data.accessToken}`,
              },
            }
          );
          if (data) {
            const { user } = data;
            if (user.partnerTypeStr === "Affiliate") {
              window.location.href = "/dashboard/affiliate";
            }
            if (user.partnerTypeStr === "IB") {
              window.location.href = "/dashboard/app";
            }
            if (user.partnerTypeStr === "Both") {
              window.location.href = "/dashboard/app";
            }
          }
        }
      }
    } catch (e) {
      console.log(e);
      enqueueSnackbar(e?.message, { variant: "error" });
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {!!errors.afterSubmit && (
          <Alert severity="error">{errors.afterSubmit.message}</Alert>
        )}
        <Box position="relative">
          <RHFTextField name="username" />
          <Box
            position="absolute"
            style={{ transform: "translate(14px, -66px)" }}
            fontSize="12px"
            bgcolor="#ffffff"
            left={0}
            zIndex={1}
            color="#637481"
          >
            Email address
          </Box>
        </Box>
        <RHFTextField
          name="password"
          // label="Password"
          type={showPassword ? "text" : "password"}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" style={{ boxShadow: "none" }}>
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  <Iconify
                    icon={showPassword ? "eva:eye-fill" : "eva:eye-off-fill"}
                  />
                </IconButton>
                <Box
                  position="absolute"
                  style={{ transform: "translate(14px, -29px)" }}
                  fontSize="12px"
                  bgcolor="#ffffff"
                  left={0}
                  zIndex={1}
                  color="#637481"
                >
                  Password
                </Box>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ my: 2 }}
      >
        <RHFCheckbox name="remember" label="Remember me" />
        <Link
          component={RouterLink}
          variant="subtitle2"
          to={PATH_AUTH.resetPassword}
        >
          Forgot password?
        </Link>
      </Stack>

      <LoadingButton
        fullWidth
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitting}
      >
        Login
      </LoadingButton>
    </FormProvider>
  );
}
